import React, { useEffect, useState, useRef } from 'react';
import { batch, useDispatch, useSelector } from 'react-redux';
import styled from '@emotion/styled';
import { useReactToPrint } from 'react-to-print';
import { format, subDays } from 'date-fns';
import { Tooltip } from 'react-tooltip';

import { BoxShadow } from '../../../../shared/GlobalStyles';
import Button from '../../../../shared/_TBComponents/Button';
import DatePicker from '../../../../shared/_TBComponents/DatePicker';
import DropDown from '../../../force-plate-dashboard/components/presentational/ForcePlateDropDown';
import OverviewChart from './OverviewChart';
import { dashboardTracker } from '../../../../shared/utils/amplitudeHelper';
import {
  fetchAthletes,
  fetchVBTData,
  setDateRange,
  setDuplicateVBTDashModal,
  setEndDateData,
  setGroup,
  setSaveVBTDashModal,
  setSelectedAthlete,
  setSelectedEndDateAnalysis,
  setSelectedEndDateOverview,
  setSelectedExercise,
  setShareVBTDashModal,
  setStartDate,
} from '../../ducks/vbtDashboardActions';
import Icon from '../../../../shared/_TBComponents/Icon';
import Text from '../../../../shared/_TBComponents/Text';
import ThresholdFilter from '../ThresholdFilter';

const AthleteContainer = styled('div')`
  @media screen and (max-width: 714px) {
    margin-bottom: 45px;
  }
  @media print {
    page-break-inside: avoid;
  }
`;

const DashboardContainer = styled('div')`
  background-color: #FFFFFF;
  border-radius: 10px;
  box-shadow: ${BoxShadow};
  display: flex;
  flex-direction: column;
  height: fit-content;
  margin: 15px 15px 7px 15px;
  padding: 16px;
  position: relative;

  @media print {
    margin: 0;
  }
`;

const DayOption = styled('button')`
  border: none;
  color: ${(props) => (!props.isActive ? '#9E9E9E' : null)};
  cursor: ${(props) => (props.readOnly ? 'default !important' : 'pointer')};
  font-family: 'Nunito Sans';
  font-size: 16px;
`;

const DaySelector = styled('div')`
  align-self: center;
  display: flex;
  margin-right: 10px;
`;

const DaySpacer = styled('span')`
  color: #9e9e9e;
  font-family: 'Nunito Sans';
  font-size: 25px;
  font-weight: 100;
  margin: 0px 3px;
`;

const GroupContainer = styled('div')`
  @media print {
    page-break-inside: avoid;
  }
`;

const IconContainer = styled('div')`
  display: flex;
  margin: 5px 0;
`;

const MenuOptionsContainer = styled('div')`
  display: flex;
  flex-wrap: wrap;
`;

const OptionsHeader = styled('div')`
  display: flex;
  flex-wrap: wrap;
  justify-content: space-between;
  margin-bottom: 10px;
`;

const SubtitleContainer = styled('div')`
  display: flex;
`;

const OverviewComponent = () => {
  const activeTemplate = useSelector((state) => state.vbtDashboard.data.activeTemplate);
  const athletes = useSelector((state) => state.vbtDashboard.data.athletes);
  const currentUser = useSelector((state) => state.auth.data.currentUser);
  const dateRange = useSelector((state) => state.vbtDashboard.data.dateRange);
  const endDateData = useSelector((state) => state.vbtDashboard.data.endDateData);
  const exercises = useSelector((state) => state.vbtDashboard.data.exercises);
  const group = useSelector((state) => state.vbtDashboard.data.group);
  const groups = useSelector((state) => state.vbtDashboard.data.groups);
  const isAthleteSelected = useSelector((state) => state.vbtDashboard.ui.isAthleteSelected);
  const isExerciseSelected = useSelector((state) => state.vbtDashboard.ui.isExerciseSelected);
  const selectedAthlete = useSelector((state) => state.vbtDashboard.data.selectedAthlete);
  const selectedExercise = useSelector((state) => state.vbtDashboard.data.selectedExercise);
  const selectedEndDateOverview = useSelector(
    (state) => state.vbtDashboard.data.selectedEndDateOverview,
  );
  const startDate = useSelector((state) => state.vbtDashboard.data.startDate);
  const vbtData = useSelector((state) => state.vbtDashboard.data.vbtData);

  const hasData = vbtData?.graphData?.groupData?.dates?.length > 0;

  const dispatch = useDispatch();

  const printRef = useRef();
  const [readyPrint, setReadyPrint] = useState(false);

  useEffect(() => {
    if (!group?.id || !isExerciseSelected) return;
    dispatch(
      fetchVBTData(
        currentUser.accountCode,
        endDateData,
        selectedExercise?.id,
        group?.id,
        startDate,
      ),
    );
  }, [endDateData, group?.id, selectedExercise, startDate]);

  const handleDuplicate = () => {
    // Amplitude tracking
    dashboardTracker('VBT Dashboard', 'Overview - Duplicate', 'Duplicate button clicked');
    dispatch(setDuplicateVBTDashModal(true));
  };

  const handleEndDate = (date) => {
    // Amplitude tracking
    dashboardTracker('VBT Dashboard', 'Overview - Date Picker', 'End date selected');
    batch(() => {
      dispatch(setEndDateData(format(new Date(date), 'yyyy-MM-dd')));
      dispatch(setSelectedEndDateAnalysis(new Date(date)));
      dispatch(setSelectedEndDateOverview(new Date(date)));
      dispatch(setStartDate(format(subDays(new Date(date), dateRange), 'yyyy-MM-dd')));
    });
  };

  const handlePrint = () => {
    // Amplitude tracking
    dashboardTracker('VBT Dashboard', 'Overview - Print', 'Print button clicked');
    setReadyPrint(true);
  };

  const handleRangeClick = (range) => {
    // Amplitude tracking
    dashboardTracker('VBT Dashboard', 'Overview - Range Picker', 'Week range selected');
    batch(() => {
      dispatch(setDateRange(range));
      dispatch(setStartDate(format(subDays(new Date(selectedEndDateOverview), range), 'yyyy-MM-dd')));
    });
  };

  const handleReactPrint = useReactToPrint({
    content: () => printRef.current,
    onBeforeGetContent: () => {
      const style = document.createElement('style');
      style.textContent = `
        @page {
          size: A4 landscape;
          margin: 2mm;
        }
        body {
          -webkit-print-color-adjust: exact;
          overflow: visible !important;
        }
        #print-athlete-chart {
          zoom: 35%;
        }
        #print-group-chart {
          zoom: 35%;
        }
      `;
      document.head.appendChild(style);
    },
    onAfterPrint: () => {
      // Amplitude tracking
      dashboardTracker('VBT Dashboard', 'Overview - Print', 'Printed');
      document.head.removeChild(document.head.lastChild);
      setReadyPrint(false);
    },
  });

  const handleSave = () => {
    // Amplitude tracking
    dashboardTracker('VBT Dashboard', 'Overview - Save', 'Save button clicked');
    dispatch(setSaveVBTDashModal(true));
  };

  const handleShare = () => {
    // Amplitude tracking
    dashboardTracker('VBT Dashboard', 'Overview - Share', 'Share button clicked');
    dispatch(setShareVBTDashModal(true));
  };

  const setAthlete = (athlete) => {
    // Amplitude tracking
    dashboardTracker('VBT Dashboard', 'Overview - Athlete Picker', 'Athlete selected');
    dispatch(setSelectedAthlete(athlete, true));
  };

  const setExercise = (exer) => {
    if (!exer.id) return;
    // Amplitude tracking
    dashboardTracker('VBT Dashboard', 'Overview - Exercise Picker', 'Exercise selected');
    dispatch(setSelectedExercise(true, exer));
  };

  const setSelectedGroup = (option) => {
    if (!option.id) return;
    // Amplitude tracking
    dashboardTracker('VBT Dashboard', 'Overview - Group Picker', 'Group selected');
    batch(() => {
      dispatch(setGroup(option));
      dispatch(setSelectedAthlete(null, false));
      dispatch(fetchAthletes(currentUser.accountCode, option.id));
    });
  };

  useEffect(() => {
    if (readyPrint) {
      setTimeout(() => {
        handleReactPrint();
      }, 1500);
    }
  }, [readyPrint]);

  return (
    <>
      <DashboardContainer ref={printRef}>
        <GroupContainer>
          <SubtitleContainer>
            <Icon
              color='#9E9E9E'
              icon='group'
              size='24px'
            />
            <Text
              styledTextProps={{
                fontSize: '19px',
                fontWeight: '700',
                marginLeft: '10px',
              }}
            >
              Group Performance
            </Text>
          </SubtitleContainer>
          <OptionsHeader>
            <MenuOptionsContainer>
              <DropDown
                defaultOption={group || {}}
                headerWidthPx={220}
                label='name'
                list={groups}
                placeholder='Select a Group'
                selectItemFunction={setSelectedGroup}
              />
              <DropDown
                defaultOption={selectedExercise || {}}
                disabled={!group?.id}
                label='name'
                list={exercises}
                headerWidthPx={220}
                placeholder='Select an Exercise'
                selectItemFunction={setExercise}
              />
              {!readyPrint ? (
                <ThresholdFilter isMulti />
              ) : null}
            </MenuOptionsContainer>
            <MenuOptionsContainer>
              <DaySelector>
                <DayOption
                  isActive={dateRange === 13}
                  onClick={() => handleRangeClick(13)}
                >
                  2W
                </DayOption>
                <DaySpacer> | </DaySpacer>
                <DayOption
                  isActive={dateRange === 27}
                  onClick={() => handleRangeClick(27)}
                >
                  4W
                </DayOption>
                <DaySpacer> | </DaySpacer>
                <DayOption
                  isActive={dateRange === 55}
                  onClick={() => handleRangeClick(55)}
                >
                  8W
                </DayOption>
              </DaySelector>
              <DatePicker
                setStartDate={handleEndDate}
                startDate={selectedEndDateOverview ? new Date(selectedEndDateOverview) : null}
                styledWrapperProps={{
                  alignSelf: 'center',
                  customBorder: '1px solid #D8D8D8',
                  customHeight: '42px',
                }}
              />
              {!readyPrint ? (
                <IconContainer>
                  {activeTemplate ? (
                    <>
                      <span
                        data-tooltip-content='Duplicate Dashboard'
                        data-tooltip-id='duplicate-tooltip'
                      >
                        <Button
                          customBorderRadius='4px'
                          iconOnly
                          iconSize={16}
                          icon='duplicate'
                          onClick={handleDuplicate}
                          styledWrapperProps={{
                            backgroundColor: '#FFFFFF',
                            border: '1px solid #D8D8D8',
                            marginLeft: '8px',
                            padding: '11px',
                          }}
                        />
                      </span>
                      <Tooltip
                        id='duplicate-tooltip'
                        place='bottom'
                        style={{
                          fontFamily: 'Nunito Sans',
                          fontSize: '13px',
                          fontStyle: 'normal',
                          fontWeight: 400,
                        }}
                      />
                    </>
                  ) : (
                    <>
                      <span
                        data-tooltip-content='Save Dashboard'
                        data-tooltip-id='save-tooltip'
                      >
                        <Button
                          customBorderRadius='4px'
                          iconOnly
                          iconSize={16}
                          icon='save2'
                          onClick={handleSave}
                          styledWrapperProps={{
                            backgroundColor: '#FFFFFF',
                            border: '1px solid #D8D8D8',
                            marginLeft: '8px',
                            padding: '11px',
                          }}
                        />
                      </span>
                      <Tooltip
                        id='save-tooltip'
                        place='bottom'
                        style={{
                          fontFamily: 'Nunito Sans',
                          fontSize: '13px',
                          fontStyle: 'normal',
                          fontWeight: 400,
                        }}
                      />
                    </>
                  )}
                  <>
                    <span
                      data-tooltip-content='Share Dashboard'
                      data-tooltip-id='share-tooltip'
                    >
                      <Button
                        customBorderRadius='4px'
                        disabled={!hasData}
                        iconOnly
                        iconSize={16}
                        icon='share-ams'
                        onClick={handleShare}
                        styledWrapperProps={{
                          backgroundColor: '#FFFFFF',
                          border: '1px solid #D8D8D8',
                          marginLeft: '8px',
                          padding: '11px',
                        }}
                      />
                    </span>
                    <Tooltip
                      id='share-tooltip'
                      hidden={!hasData}
                      place='bottom'
                      style={{
                        fontFamily: 'Nunito Sans',
                        fontSize: '13px',
                        fontStyle: 'normal',
                        fontWeight: 400,
                      }}
                    />
                  </>
                  <>
                    <span
                      data-tooltip-content='Print Dashboard'
                      data-tooltip-id='print-tooltip'
                    >
                      <Button
                        customBorderRadius='4px'
                        disabled={!hasData}
                        iconOnly
                        iconSize={16}
                        icon='printer'
                        onClick={handlePrint}
                        styledWrapperProps={{
                          backgroundColor: '#FFFFFF',
                          border: '1px solid #D8D8D8',
                          marginLeft: '8px',
                          padding: '11px',
                        }}
                      />
                    </span>
                    <Tooltip
                      id='print-tooltip'
                      hidden={!hasData}
                      place='bottom'
                      style={{
                        fontFamily: 'Nunito Sans',
                        fontSize: '13px',
                        fontStyle: 'normal',
                        fontWeight: 400,
                      }}
                    />
                  </>
                </IconContainer>
              ) : null}
            </MenuOptionsContainer>
          </OptionsHeader>
          {/* Group  Chart */}
          <OverviewChart />
        </GroupContainer>
        {readyPrint && !isAthleteSelected ? null : (
          <AthleteContainer>
            <SubtitleContainer style={{ marginTop: '10px' }}>
              <Icon
                color='#9E9E9E'
                icon='user'
                size='20px'
              />
              <Text
                styledTextProps={{
                  fontSize: '19px',
                  fontWeight: '700',
                  marginLeft: '10px',
                }}
              >
                Athlete Performance
              </Text>
            </SubtitleContainer>
            <MenuOptionsContainer style={{ marginBottom: '10px' }}>
              <DropDown
                defaultOption={selectedAthlete || {}}
                disabled={!group?.id}
                label='fullName'
                list={athletes}
                headerWidthPx={220}
                placeholder='Select an Athlete'
                readOnly
                selectItemFunction={setAthlete}
              />
            </MenuOptionsContainer>
            {/* Athlete Chart */}
            <OverviewChart isAthlete />
          </AthleteContainer>
        )}
      </DashboardContainer>
    </>
  );
};

export default OverviewComponent;
